import { useState, useEffect, useCallback } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, ChevronDown, BarChart3, Eye, Upload, Trash2, Music } from "lucide-react";
import { API, API_BASE } from "@/lib/api";

export default function AdminBreathingRoom() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [analytics, setAnalytics] = useState([]);
  const [audioFiles, setAudioFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [settings, stats, audio] = await Promise.all([
        http.get("/breathing-room/admin/settings"),
        http.get("/breathing-room/admin/analytics"),
        http.get("/breathing-room/admin/audio"),
      ]);
      setS(settings.data);
      setAnalytics(stats.data);
      setAudioFiles(audio.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try { await http.patch("/breathing-room/admin/settings", s); toast.success("Saved"); }
    catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const preview = () => {
    window.open("/breathing-room?preview=true", "_blank");
  };

  const uploadAudio = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const token = localStorage.getItem("stc_token");
      const resp = await fetch(`${API}/breathing-room/admin/upload-audio`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!resp.ok) throw new Error("Upload failed");
      toast.success("Audio uploaded");
      load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const deleteAudio = async (id) => {
    if (!window.confirm("Delete this audio file?")) return;
    await http.delete(`/breathing-room/admin/audio/${id}`);
    load();
  };

  if (loading || !s) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#C9A055]" /></div>;

  const Field = ({ label, field, rows }) => (
    <div>
      <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">{label}</label>
      {rows ? (
        <textarea value={s[field] || ""} onChange={e => setS(p => ({ ...p, [field]: e.target.value }))} rows={rows}
          className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none resize-none" />
      ) : (
        <input value={s[field] || ""} onChange={e => setS(p => ({ ...p, [field]: e.target.value }))}
          className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" />
      )}
    </div>
  );

  const sections = [
    { title: "Landing Page", fields: () => <>
      <Field label="Headline" field="headline" rows={2} />
      <Field label="Supporting Text" field="supporting_text" rows={3} />
      <Field label="Primary Button" field="primary_btn" />
      <Field label="Reassurance" field="reassurance" />
    </> },
    { title: "Exercise Selector", fields: () => <>
      <Field label="Selector Heading" field="selector_heading" />
      <Field label="Choose For Me Button" field="choose_for_me_btn" />
    </> },
    { title: "After Exercise", fields: () => <>
      <Field label="After Heading" field="after_heading" />
      <Field label="After Message" field="after_message" rows={2} />
      <Field label="Completion Message" field="completion_message" />
      <Field label="Stop Message" field="stop_message" rows={2} />
    </> },
    { title: "Safety & Privacy", fields: () => <>
      <Field label="Safety Notice" field="safety_notice" rows={5} />
      <Field label="Crisis Notice" field="crisis_notice" rows={3} />
      <Field label="Journal Disclaimer" field="journal_disclaimer" rows={2} />
    </> },
  ];

  return (
    <div className="space-y-4" data-testid="admin-breathing-room">
      <div className="flex justify-between items-center">
        <h2 className="font-display text-3xl text-[#F4F1EA]">Breathing Room</h2>
        <div className="flex gap-2">
          <button onClick={preview} data-testid="br-preview-btn"
            className="flex items-center gap-2 border border-[#C9A055] text-[#C9A055] px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#C9A055] hover:text-[#1A1A1A] transition-all">
            <Eye size={14} /> Preview
          </button>
          <button onClick={save} disabled={saving}
            className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-5 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37] disabled:opacity-50" data-testid="br-save-settings">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save All
          </button>
        </div>
      </div>

      {/* Analytics */}
      {analytics.length > 0 && (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
          <p className="text-zinc-400 text-xs uppercase tracking-wider mb-3 flex items-center gap-2"><BarChart3 size={12} /> Exercise Analytics</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {analytics.map((a, i) => (
              <div key={i} className="text-center">
                <p className="font-display text-2xl text-[#C9A055]">{a.count}</p>
                <p className="text-zinc-500 text-[10px]">{a.exercise_id?.replace(/_/g, " ")} — {a.event}s</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audio Management */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-4" data-testid="br-audio-mgmt">
        <div className="flex justify-between items-center">
          <p className="text-zinc-400 text-xs uppercase tracking-wider flex items-center gap-2"><Music size={12} /> Voice Guidance Audio</p>
          <label className="flex items-center gap-2 border border-[#C9A055] text-[#C9A055] px-3 py-1.5 rounded-lg text-[10px] font-medium cursor-pointer hover:bg-[#C9A055] hover:text-[#1A1A1A] transition-all">
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {uploading ? "Uploading..." : "Upload Audio"}
            <input type="file" accept="audio/*,.mp3,.wav,.ogg,.m4a" className="hidden" onChange={uploadAudio} disabled={uploading} data-testid="br-audio-upload" />
          </label>
        </div>
        <p className="text-zinc-600 text-[10px]">Upload MP3/WAV files for custom voice guidance cues. Currently using gentle tone synthesis.</p>
        {audioFiles.length === 0 ? (
          <p className="text-zinc-600 text-xs text-center py-4">No custom audio files uploaded. Default tone guidance is active.</p>
        ) : (
          <div className="space-y-2">
            {audioFiles.map(a => (
              <div key={a.id} className="flex items-center justify-between gap-3 rounded-lg border border-white/[0.06] bg-white/[0.01] px-3 py-2">
                <div className="flex items-center gap-3 min-w-0">
                  <Music size={14} className="text-[#C9A055] shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[#F4F1EA] text-xs truncate">{a.filename}</p>
                    <p className="text-zinc-600 text-[10px]">{(a.size / 1024).toFixed(0)} KB · {new Date(a.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <audio controls src={`${API_BASE}${a.url}`} className="h-8 w-32" />
                  <button onClick={() => deleteAudio(a.id)} className="text-zinc-500 hover:text-red-400 p-1"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {sections.map((sec, i) => (
        <div key={i} className="rounded-xl border border-white/[0.06] overflow-hidden">
          <button onClick={() => setOpenSection(openSection === i ? null : i)} className="w-full flex items-center justify-between p-4 text-left hover:bg-white/[0.02] transition-colors">
            <span className="text-[#F4F1EA] text-sm font-medium">{sec.title}</span>
            <ChevronDown size={14} className={`text-zinc-500 transition-transform ${openSection === i ? "rotate-180" : ""}`} />
          </button>
          {openSection === i && <div className="p-4 pt-0 space-y-3 border-t border-white/[0.04]">{sec.fields()}</div>}
        </div>
      ))}
    </div>
  );
}
